// ClikMeet's backgrounds (Ravi, 26 Sep: "background options and background blur"): blur, light or strong; six pictures
// of our own — four rooms made for us (OpenAI gpt-image-2, 26 Sep 2026, no people or text) and two in ClikMeet's colours
// (drawn with ffmpeg); or a picture the person adds, kept on this device
// only. Done in the browser by our own compositor (compositor.js) on MediaPipe's selfie segmenter, served from meet.clikwork.co
// itself — nothing is fetched from Google or a CDN. ClikMeet only: Canvas has none of it.
// The compositor and MediaPipe (~140 KB) load only when someone picks a background: every other visitor — Canvas's
// too — downloads none of it (scripts/build.mjs splits it into its own file).
const compositor = () => import("./compositor.js"); // ours (26 Sep), with MediaPipe inside it

// Two models, chosen by the camera's shape (compositor.js modelFor): widescreen (256 × 144) for a laptop or a phone on
// its side, the square one (256 × 256) for a phone held upright — neither squeezes a picture into the wrong shape.
export const ASSETS = { tasksVisionFileSet: "/vendor/mediapipe/wasm",
  modelAssetPath: "/vendor/mediapipe/selfie_segmenter_landscape.tflite", squareModelPath: "/vendor/mediapipe/selfie_segmenter.tflite" };
const PICTURES = [
  ["office", "Office"], ["lounge", "Lounge"], ["bookshelf", "Bookshelf"], ["window", "Window"], ["ink", "Ink"], ["indigo", "Indigo"],
];
// What the picker offers, in order. `id` is what is remembered; a picture's image is /backgrounds/<id>.jpg.
export const CHOICES = [
  { id: "none", label: "None" },
  { id: "blur-light", label: "Slight blur", blur: 8 },
  { id: "blur-strong", label: "Blur", blur: 20 },
  ...PICTURES.map(([id, label]) => ({ id, label, image: `/backgrounds/${id}.jpg`, thumb: `/backgrounds/thumbs/${id}.jpg` })),
];
const KEY = "clikmeet.background", OWN = "clikmeet.background.own";
// Auto lighting (Ravi, 28 Sep: "apply auto lighting"): on for everyone, off per device from the same dialog.
const LIGHT_KEY = "clikmeet.lighting";
export const LIGHTING = [{ id: "auto", label: "Auto" }, { id: "off", label: "Off" }];
export function lighting(store = globalThis.localStorage) {
  try { return store?.getItem(LIGHT_KEY) === "off" ? "off" : "auto"; } catch { return "auto"; }
}
export function rememberLighting(id, store = globalThis.localStorage) {
  try { store?.setItem(LIGHT_KEY, id === "off" ? "off" : "auto"); return true; } catch { return false; }
}
const OWN_MAX = 1280; // an added picture is kept at most this wide AND this tall — small enough for this browser's storage

// What our compositor needs: WebGL2 (it draws), a canvas that can be a camera (captureStream), and MediaPipe's
// WebAssembly.
// ON for everyone (Ravi, 26 Sep: "pls switch on the background") now our own compositor draws it. One browser can still
// turn it off for itself with localStorage "clikmeet.bg.preview" = "0" (a way out if a device draws it badly).
export const PREVIEW_KEY = "clikmeet.bg.preview";
export function enabled(store = globalThis.localStorage) {
  try { return store?.getItem(PREVIEW_KEY) !== "0"; } catch { return true; }
}
let can = null;
export function supported() {
  if (!enabled()) return false;
  if (can !== null) return can;
  try {
    can = typeof WebAssembly === "object" && typeof HTMLCanvasElement !== "undefined" && "captureStream" in HTMLCanvasElement.prototype &&
      Boolean(document.createElement("canvas").getContext("webgl2"));
  } catch { can = false; }
  return can;
}

// The processor's settings for a choice (pure; tested). Answers null for "none", or a choice this device cannot show.
export function modeFor(id, own = null) {
  const c = CHOICES.find((x) => x.id === id);
  if (c?.blur) return { mode: "background-blur", blurRadius: c.blur };
  if (c?.image) return { mode: "virtual-background", imagePath: c.image };
  if (id === "own" && own) return { mode: "virtual-background", imagePath: own };
  return null;
}

// What the processor is made with, for a background choice and the lighting together (pure; tested): the background's
// settings with the lighting on them, lighting alone when there is no background, or null when there is neither.
export function settingsFor(id, own = null, light = "auto") {
  const bg = modeFor(id, own);
  if (bg) return { ...bg, lighting: light };
  return light === "auto" ? { mode: "lighting", lighting: "auto" } : null;
}

// This device's choice, remembered between meetings. Storage can be missing (a private window): then nothing is kept.
export function saved(store = globalThis.localStorage) {
  try { return { id: store?.getItem(KEY) || "none", own: store?.getItem(OWN) || null }; } catch { return { id: "none", own: null }; }
}
// The picture first, then the choice that names it: a full store never leaves "own" pointing at an old picture or none
// (re-audit N3). Answers false when this device could not keep it — the choice still applies to this meeting.
export function remember(id, own, store = globalThis.localStorage) {
  try {
    if (own !== undefined) own ? store?.setItem(OWN, own) : store?.removeItem(OWN);
    store?.setItem(KEY, id);
    return true;
  } catch { return false; }
}

// A processor for a choice, made before a camera is published (LiveKit applies it before anything is sent — re-audit
// N1: a camera turned on mid-call used to go out plain until the background caught up). Null for "none".
export async function processorFor(id, own = null, light = lighting()) {
  const opts = settingsFor(id, own, light);
  if (!opts || !supported()) return null;
  const { Compositor } = await compositor();
  return new Compositor({ ...opts, assets: ASSETS });
}

// Put a choice on a camera track (a LiveKit LocalVideoTrack). One processor per track, switched between modes so the
// picture never flickers; "none" takes it off. Choices on one track run one after another, and each run applies the
// LATEST choice — Ink then None, pressed while Ink is still loading, ends at None (re-audit N2). Answers true when the
// latest choice is showing.
const queue = new WeakMap(), latest = new WeakMap();
export function apply(track, id, own = null, light = lighting()) {
  if (!track) return Promise.resolve(false);
  latest.set(track, { id, own, light });
  const run = () => { const w = latest.get(track); return applyNow(track, w.id, w.own, w.light).then((ok) => ({ ok, id: w.id })); };
  const p = (queue.get(track) || Promise.resolve()).then(run, run);
  queue.set(track, p.catch(() => {}));
  return p.then((r) => r.ok && r.id === id);
}
// Whatever is being applied to this track, finished (the lobby waits for it before joining).
export function settled(track) { return (track && queue.get(track)) || Promise.resolve(); }

async function applyNow(track, id, own, light) {
  const opts = settingsFor(id, own, light);
  const current = track.getProcessor?.();
  if (!opts) {
    if (current) await track.stopProcessor();
    return id === "none";
  }
  if (!supported()) return false;
  if (current?.switchTo) { await current.switchTo(opts); return true; }
  const { Compositor } = await compositor();
  await track.setProcessor(new Compositor({ ...opts, assets: ASSETS }));
  return true;
}

// A picture the person adds: read, made at most OWN_MAX wide, as a JPEG data URL (never uploaded anywhere).
export async function ownPicture(file) {
  if (!file || !/^image\/(jpeg|png|webp)$/.test(file.type)) throw new Error("Choose a JPEG, PNG or WebP picture.");
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, OWN_MAX / bitmap.width, OWN_MAX / bitmap.height);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close?.();
  return canvas.toDataURL("image/jpeg", 0.85);
}
